// blockInspector.js - the pure block-type -> contextual-inspector map (SP6 Task 5).
//
// BoardReportInspector needs to know, for whichever block is currently selected on the report
// canvas, WHAT to show: an editable field set (for narrative-driven blocks) or a read-only data
// panel (for chart/figure blocks). This module is the single source of truth for that mapping -
// pure, so it's trivially unit-testable without mounting anything.
//
// `inspectorFor(blockType, binding) -> { kind, title, fields?, sourceMetricKeys?, dataEndpoint? }`
//   kind: 'edit' - a narrative-editing block; render `fields` (see below) + a read-only
//                  "Source data" panel of `sourceMetricKeys` (METRIC_CATALOG keys, resolved via
//                  the `metrics` map BoardReportInspector already has).
//         'data' - a chart/figure block; render a read-only panel sourced from `dataEndpoint`
//                  (relative to /board-reports/{id}/runs/{runId}/, same convention as
//                  METRIC_CATALOG's `endpoint` field in reportMetrics.js).
//         'note' - a notes-family block; render a read-only note panel from the run's editable
//                  `binding.text` (notes) or the config's `staticText` (appendix_notes/
//                  notes_confidence), never a "Data behind this" figure panel.
//         'driverOverview' - the driver_table block selected as a whole; render a READ-ONLY list of
//                  every driver whose rows drill into a single driver's editable annotation (kind
//                  'edit' with the drivers field's `onlyIndex` set).
//   fields: an ordered list of `{ type, narrativeKey? }` the inspector renders via the
//           inspector/fields/* components:
//             'summary' - one page_summary's bound summary (SummaryField, narrativeKey required)
//             'text'    - a free-text narrative field that may take @figure tokens (TextField)
//             'plain'   - a plain, token-free input (PlainField) - decision_owner/deadline
//             'bullets' - the exec-narrative bullets list (BulletsField, no narrativeKey)
//             'drivers' - the driver-annotation rows (DriverAnnotations, no narrativeKey)
//
// PAGE_SUMMARY_SOURCE_KEYS - which METRIC_CATALOG keys back each of the 4 per-page summaries
// (boardreport.Narrative's overview_summary/performance_summary/drivers_summary/forecast_summary -
// see NarrativeRail.jsx/PageSummaryBlock.jsx). Keyed by `binding.summaryKey`, the same field
// page_summary blocks already read to pick which summary they render (ManifestPage's page.blocks
// binding, PageSummaryBlock.jsx).
const PAGE_SUMMARY_SOURCE_KEYS = {
  overview_summary: ["total_spend", "cloud_total", "ai_total", "mom_pct"],
  performance_summary: ["cloud_total", "ai_total", "mom_pct"],
  drivers_summary: ["top_driver_name", "top_driver_delta"],
  forecast_summary: ["forecast_midpoint", "forecast_low", "forecast_high", "cap"],
};

// CHART_BLOCK_ENDPOINTS - the figure endpoint (relative to .../runs/{runId}/, matching
// METRIC_CATALOG's `endpoint` convention in reportMetrics.js) each read-only chart/table block
// type fetches. A block type present here always resolves to `kind:'data'`.
const CHART_BLOCK_ENDPOINTS = {
  stacked_trend: "figures/trend",
  variance_waterfall: "figures/pvm",
  forecast_band: "figures/forecast-band",
  risk_bridge: "figures/risk-bridge",
  performance_table: "figures/performance",
  kpi_strip: "figures/kpis",
  horizon_table: "figures/horizon",
  forecast_monthly_table: "figures/forecast-monthly",
  data_quality: "figures/data-quality",
  detailed_cost: "figures/detailed-cost",
  // Budget Variance & Risk report blocks (each fetches the same endpoint its block
  // component does — see the matching useBlockData calls in blocks/Budget*Block.jsx /
  // PreventiveActionsBlock.jsx). Endpoint path skews from the block-type name (no
  // "-table" suffix on variance/ownership), exactly as the block components use.
  budget_risk_overview: "figures/budget-risk-overview",
  budget_variance_table: "figures/budget-variance",
  budget_ownership_table: "figures/budget-ownership",
  preventive_actions: "figures/preventive-actions",
  // Optimization & Savings report blocks - float euros (no cents scaling), matching the
  // matching block components' useBlockData calls in blocks/Savings*Block.jsx.
  savings_summary_kpis: "figures/savings-summary",
  implemented_actions_table: "figures/implemented-actions",
  open_opportunities_table: "figures/open-opportunities",
  savings_verification_table: "figures/savings-verification",
};

// humanizeBlockType - "variance_waterfall" -> "Variance waterfall" (title-fallback for a
// recognized chart block type that has no more specific title of its own to hand the inspector).
function humanizeBlockType(blockType) {
  return String(blockType)
    .split("_")
    .filter(Boolean)
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/**
 * inspectorFor(blockType, binding) -> inspector config. Pure, never throws - an unrecognized
 * `blockType` (including undefined/null) gracefully falls back to a `data` panel with
 * `dataEndpoint: null` (BoardReportInspector renders a "no data" note for that case) rather than
 * blowing up the inspector when a manifest carries a block type this map doesn't know about yet.
 */
export function inspectorFor(blockType, binding = {}) {
  const safeBinding = binding || {};

  switch (blockType) {
    case "page_summary": {
      const { summaryKey } = safeBinding;
      return {
        kind: "edit",
        title: "Summary",
        fields: [{ type: "summary", narrativeKey: summaryKey }],
        sourceMetricKeys: PAGE_SUMMARY_SOURCE_KEYS[summaryKey] || [],
      };
    }

    case "exec_narrative":
      return {
        kind: "edit",
        title: "Executive summary",
        fields: [
          { type: "bullets" },
          { type: "text", narrativeKey: "what_changed" },
          { type: "text", narrativeKey: "why" },
          { type: "text", narrativeKey: "what_to_do" },
        ],
        sourceMetricKeys: ["total_spend", "cloud_total", "ai_total", "mom_pct"],
      };

    case "decision":
      return {
        kind: "edit",
        title: "Decision",
        fields: [
          { type: "text", narrativeKey: "decision_required" },
          { type: "plain", narrativeKey: "decision_owner" },
          { type: "plain", narrativeKey: "decision_deadline" },
        ],
        sourceMetricKeys: ["forecast_midpoint", "cap"],
      };

    case "driver_table": {
      // Two-level flow. Selecting the block as a whole (no driverIndex) shows a READ-ONLY overview
      // of every driver ('driverOverview') whose rows drill into one driver. A per-row selection
      // (ManifestPage passes { ...binding, driverIndex } from a canvas row click, or the overview
      // list drills in the same way) opens JUST that driver's editable annotation. No top-driver
      // source panel on the single view - the driver's own name/impact already head the card.
      const hasIndex = safeBinding.driverIndex != null;
      if (hasIndex) {
        return {
          kind: "edit",
          title: "Driver annotation",
          fields: [{ type: "drivers", onlyIndex: safeBinding.driverIndex }],
          sourceMetricKeys: [],
        };
      }
      return { kind: "driverOverview", title: "Key drivers" };
    }

    // Notes-family blocks carry no figure endpoint, so they render a read-only note panel rather
    // than "Data behind this". `notes` shows the run's editable free text (binding.text, the same
    // field NotesBlock reads); the other two are standing/computed content with no per-run binding,
    // so they show a short description instead of a bare "No data available".
    case "notes":
      return { kind: "note", title: "Notes" };
    case "appendix_notes":
      return {
        kind: "note",
        title: "Notes & assumptions",
        staticText:
          "Standing methodology & assumptions notes — the same on every run, not specific to this period.",
      };
    case "notes_confidence":
      return {
        kind: "note",
        title: "Confidence & assumptions",
        staticText:
          "Forecast confidence and calibration footnote, computed live from the calibration report.",
      };

    default: {
      if (Object.prototype.hasOwnProperty.call(CHART_BLOCK_ENDPOINTS, blockType)) {
        return {
          kind: "data",
          title: humanizeBlockType(blockType),
          dataEndpoint: CHART_BLOCK_ENDPOINTS[blockType],
        };
      }
      // Unknown block type (including undefined/null) - graceful "no data" fallback rather than
      // throwing; BoardReportInspector renders a plain note for a null dataEndpoint.
      return {
        kind: "data",
        title: blockType == null ? "" : String(blockType),
        dataEndpoint: null,
      };
    }
  }
}

export default inspectorFor;
